import React, { useEffect, useState } from "react";
import { CreditCard, Plus, Trash2, Edit, Check } from "lucide-react";
import AddPaymentMethodModal from "./components/Modal/AddPaymentMethod";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";
import PaymentMethodLoader from "./components/Loader";

interface paymentMethodTypes {
  _id: string;
  userId: string;
  companyId: string;
  paymentMethodType: string;
  paymentMethodId: string;
  customerId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
  createdAt: string;
}

const PaymentMethods = () => {
  const { user } = useUserInfo();
  const AppService = useAppServices();
  const [loading, setLoading] = useState<boolean>(true);
  const [paymentMethods, setPaymentMethods] = useState<paymentMethodTypes[]>(
    []
  );

  const getPaymentMethods = async () => {
    setLoading(true);
    const { response } = await AppService.payment_method.filter({
      query: `companyId=${user?.companyId}`,
    });

    if (response) setPaymentMethods(response.data);
    else setPaymentMethods([]);
    setLoading(false);
  };
  let pageLoad = true;
  useEffect(() => {
    if (pageLoad) {
      getPaymentMethods();
      pageLoad = false;
    }
  }, []);

  const setAsDefault = async (_id: string) => {
    const { response } = await AppService.payment_method.set_default({
      payload: { _id, companyId: user?.companyId },
      toaster: true,
    });
    if (response) {
      setPaymentMethods((methods) =>
        methods.map((method) => ({
          ...method,
          isDefault: method._id === _id,
        }))
      );
    }
  };

  const removeMethod = async (_id: string) => {
    const { response } = await AppService.payment_method.delete({
      query: `_id=${_id}`,
      toaster: true,
    });
    if (response) getPaymentMethods();
  };

  const getCardIcon = (type: string) => {
    const baseClasses =
      "w-8 h-8 rounded-lg flex items-center justify-center shadow-sm";
    switch (type?.toLowerCase()) {
      case "visa":
        return `${baseClasses} bg-blue-500 text-white`;
      case "mastercard":
        return `${baseClasses} bg-red-500 text-white`;
      default:
        return `${baseClasses} bg-gray-500 text-white`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Payment Methods
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage your payment cards and billing
          </p>
        </div>
        <AddPaymentMethodModal handleRefresh={getPaymentMethods} />
      </div>

      {/* Loader or Payment Methods */}
      {loading ? (
        <PaymentMethodLoader />
      ) : paymentMethods.length > 0 ? (
        <div className="grid gap-6">
          {paymentMethods.map((method: paymentMethodTypes) => (
            <div
              key={method._id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 hover:shadow-md dark:hover:shadow-gray-900/20 transition-all duration-200 group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className={getCardIcon(method.brand)}>
                    <CreditCard size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                        {method.brand} •••• {method.last4}
                      </h3>
                      {method.isDefault && (
                        <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                          <Check size={12} />
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Expires {method.expMonth}/{method.expYear}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {!method.isDefault && (
                    <>
                      <button
                        onClick={() => setAsDefault(method._id)}
                        className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                      >
                        <Check size={16} />
                        Set Default
                      </button>
                      <button
                        onClick={() => removeMethod(method._id)}
                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg transition-colors duration-200"
                        title="Remove card"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700">
          <CreditCard
            size={48}
            className="mx-auto text-gray-300 dark:text-gray-600 mb-4"
          />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No payment methods
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Add a payment method to make seamless payments and enable
            auto-recharge features.
          </p>
          <button className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200">
            <Plus size={20} />
            <span>Add Your First Payment Method</span>
          </button>
        </div>
      )}

      {/* Security Notice */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-lg">
            <Check size={16} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 text-sm">
              Secure & Encrypted
            </h4>
            <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
              Your payment information is securely stored and encrypted. We
              never share your details with third parties.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;
